// Admin → Flyers: upload images/PDFs that appear on /flyers and in the
// Credit Union ▸ Flyers menu. Built-in flyers stay fixed at the top of the page.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ExternalLink, FileText, Image as ImageIcon, Pencil, Trash2, UploadCloud, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { assetUrl, loadFlyers } from "../../lib/flyers";
import { Switch } from "../ui/switch";
import { Field, Overlay, inputCls } from "./boardShared";

const MAX_MB = 25;
const EXTS = ["jpg", "jpeg", "png", "webp", "pdf"];
const errMsg = (e) => formatApiError(e.response?.data?.detail) || "Something went wrong";
const sizeLabel = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const switchCls = "data-[state=checked]:bg-bdu-gold data-[state=unchecked]:bg-bdu-cream/20";
const titleFromFile = (name) => name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs text-bdu-cream/70 cursor-pointer">
      <Switch checked={checked} onCheckedChange={onChange} className={switchCls} data-testid={testId} /> {label}
    </label>
  );
}

function Selects({ form, set, sections, shareTargets, prefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Section *">
        <select value={form.section} onChange={(e) => set("section", e.target.value)} className={inputCls} data-testid={`${prefix}-section`}>
          {Object.entries(sections).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>
      <Field label="Share button sends people to">
        <select value={form.share_path} onChange={(e) => set("share_path", e.target.value)} className={inputCls} data-testid={`${prefix}-share`}>
          {Object.entries(shareTargets).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </Field>
    </div>
  );
}

const EMPTY = { title: "", description: "", section: "credit_union", share_path: "/flyers", show_in_menu: true, visible: true };

function UploadCard({ sections, shareTargets, onUploaded }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pick = (f) => {
    if (!f) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!EXTS.includes(ext)) { toast.error("Upload a JPG, PNG, WEBP or PDF file."); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Files must be ${MAX_MB} MB or smaller.`); return; }
    setFile(f);
    setForm((p) => ({ ...p, title: p.title || titleFromFile(f.name) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a file first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, String(v)));
    setProgress(0);
    try {
      await api.post("/admin/flyers", fd, {
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      toast.success("Flyer uploaded and live");
      setFile(null);
      setForm(EMPTY);
      onUploaded();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setProgress(null); }
  };

  const busy = progress !== null;
  return (
    <form onSubmit={submit} className="border border-bdu-gold/25 bg-bdu-surface2 p-6 space-y-5" data-testid="admin-flyers-upload">
      <label
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
        className={`flex flex-col items-center justify-center gap-2 border border-dashed px-6 py-8 text-center cursor-pointer transition-colors ${drag ? "border-bdu-gold bg-bdu-gold/5" : "border-bdu-gold/30 hover:border-bdu-gold/60"}`}
        data-testid="admin-flyers-dropzone"
      >
        <UploadCloud size={26} className="text-bdu-gold" />
        {file ? (
          <span className="text-sm text-bdu-cream" data-testid="admin-flyers-file-name">{file.name} · {sizeLabel(file.size)}</span>
        ) : (
          <span className="text-sm text-bdu-cream/70">Drop a flyer here or <span className="text-bdu-gold underline">browse</span></span>
        )}
        <span className="text-[11px] text-bdu-cream/45">JPG, PNG, WEBP or PDF · up to {MAX_MB} MB · PDFs get page previews automatically</span>
        <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
               data-testid="admin-flyers-file-input" />
      </label>

      <Field label="Title *">
        <input required minLength={2} maxLength={120} value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls}
               placeholder="e.g. Juneteenth Membership Drive" data-testid="admin-flyers-title" />
      </Field>
      <Field label="Short description (shown on the Flyers page)">
        <textarea rows={2} maxLength={600} value={form.description} onChange={(e) => set("description", e.target.value)}
                  className={`${inputCls} resize-none`} data-testid="admin-flyers-description" />
      </Field>
      <Selects form={form} set={set} sections={sections} shareTargets={shareTargets} prefix="admin-flyers" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-6">
          <Toggle label="Show in Flyers menu" checked={form.show_in_menu} onChange={(v) => set("show_in_menu", v)} testId="admin-flyers-menu-toggle" />
          <Toggle label="Visible on Flyers page" checked={form.visible} onChange={(v) => set("visible", v)} testId="admin-flyers-visible-toggle" />
        </div>
        <button type="submit" disabled={busy} className="btn-gold text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="admin-flyers-submit">
          <UploadCloud size={14} /> {busy ? (progress < 100 ? `Uploading ${progress}%` : "Making previews…") : "Upload flyer"}
        </button>
      </div>
      {busy && (
        <div className="h-1 bg-bdu-cream/10" data-testid="admin-flyers-progress">
          <div className="h-1 bg-bdu-gold transition-[width] duration-200" style={{ width: `${progress}%` }} />
        </div>
      )}
    </form>
  );
}

function EditModal({ flyer, sections, shareTargets, onClose, onSaved }) {
  const [form, setForm] = useState({ title: flyer.title, description: flyer.description, section: flyer.section, share_path: flyer.share_path });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/flyers/${flyer.id}`, form);
      toast.success("Flyer updated");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} testId="admin-flyers-edit-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="relative max-w-xl w-full bg-bdu-bg border border-bdu-gold/40 p-6 sm:p-8 space-y-4">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-bdu-cream/60 hover:text-bdu-cream"><X size={18} /></button>
        <div className="label-eyebrow">Edit flyer</div>
        <Field label="Title *">
          <input required minLength={2} maxLength={120} value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="admin-flyers-edit-title" />
        </Field>
        <Field label="Short description">
          <textarea rows={3} maxLength={600} value={form.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} resize-none`} data-testid="admin-flyers-edit-description" />
        </Field>
        <Selects form={form} set={set} sections={sections} shareTargets={shareTargets} prefix="admin-flyers-edit" />
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="btn-gold text-xs disabled:opacity-50" data-testid="admin-flyers-edit-save">{busy ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Overlay>
  );
}

function FlyerRow({ f, first, last, shareTargets, onChange, onEdit }) {
  const call = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  const patch = (body) => call(() => api.patch(`/admin/flyers/${f.id}`, body));
  const move = (direction) => call(() => api.post(`/admin/flyers/${f.id}/move`, { direction }));
  const remove = () => window.confirm(`Delete "${f.title}"? It will be removed from the Flyers page and menu.`)
    && call(() => api.delete(`/admin/flyers/${f.id}`), "Flyer deleted");
  const iconBtn = "p-2 border border-bdu-gold/20 text-bdu-cream/60 hover:text-bdu-gold hover:border-bdu-gold/60 disabled:opacity-30 disabled:pointer-events-none";
  return (
    <div className={`flex flex-wrap items-center gap-4 border border-bdu-gold/15 bg-bdu-surface2 p-3 ${f.visible ? "" : "opacity-60"}`} data-testid={`admin-flyer-${f.id}`}>
      <a href={assetUrl(f.file_url)} target="_blank" rel="noopener noreferrer" className="w-16 h-20 shrink-0 bg-white/5 ring-1 ring-bdu-gold/20 overflow-hidden">
        {f.preview_urls[0] && <img src={assetUrl(f.preview_urls[0])} alt="" className="w-full h-full object-cover object-top" loading="lazy" />}
      </a>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-bdu-cream font-serif truncate" data-testid={`admin-flyer-${f.id}-title`}>{f.title}</div>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-bdu-cream/50">
          {f.file_type === "pdf" ? <FileText size={12} /> : <ImageIcon size={12} />}
          {f.file_type === "pdf" ? `PDF · ${f.page_count} page${f.page_count > 1 ? "s" : ""}` : "Image"} · {sizeLabel(f.size)}
          <span>· Shares to {shareTargets[f.share_path] || f.share_path}</span>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-5">
        <Toggle label="Menu" checked={f.show_in_menu} onChange={(v) => patch({ show_in_menu: v })} testId={`admin-flyer-${f.id}-menu`} />
        <Toggle label="Visible" checked={f.visible} onChange={(v) => patch({ visible: v })} testId={`admin-flyer-${f.id}-visible`} />
        <div className="flex gap-1">
          <button type="button" disabled={first} onClick={() => move("up")} className={iconBtn} title="Move up" data-testid={`admin-flyer-${f.id}-up`}><ArrowUp size={13} /></button>
          <button type="button" disabled={last} onClick={() => move("down")} className={iconBtn} title="Move down" data-testid={`admin-flyer-${f.id}-down`}><ArrowDown size={13} /></button>
          <a href={`/flyers#flyer-${f.id}`} target="_blank" rel="noopener noreferrer" className={iconBtn} title="View on Flyers page"><ExternalLink size={13} /></a>
          <button type="button" onClick={onEdit} className={iconBtn} title="Edit" data-testid={`admin-flyer-${f.id}-edit`}><Pencil size={13} /></button>
          <button type="button" onClick={remove} className={`${iconBtn} hover:text-red-300 hover:border-red-400/50`} title="Delete" data-testid={`admin-flyer-${f.id}-delete`}><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

export default function FlyersTab() {
  const [data, setData] = useState({ flyers: [], sections: {}, share_targets: {} });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/flyers");
      setData(r.data);
      loadFlyers(true);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const { flyers, sections, share_targets: shareTargets } = data;
  return (
    <div className="space-y-10" data-testid="admin-flyers-tab">
      <div>
        <div className="label-eyebrow">Flyers</div>
        <h3 className="mt-2 font-serif text-2xl text-bdu-cream">Upload flyers to the Flyers page &amp; menu</h3>
        <p className="mt-1 text-sm text-bdu-cream/55 max-w-3xl">
          Uploads appear on <a href="/flyers" target="_blank" rel="noopener noreferrer" className="text-bdu-gold hover:underline">/flyers</a> under
          their section, and in Credit Union ▸ Flyers when "Show in menu" is on. The built-in flyers (Circulate the Dollar, Rally of the Minds,
          social media) always stay at the top.
        </p>
      </div>

      {!loading && <UploadCard sections={sections} shareTargets={shareTargets} onUploaded={load} />}

      <div className="space-y-8" data-testid="admin-flyers-list">
        {loading && <div className="text-xs text-bdu-cream/50">Loading…</div>}
        {!loading && flyers.length === 0 && (
          <div className="border border-bdu-gold/15 bg-bdu-surface2 p-5 text-sm italic text-bdu-cream/55">No uploaded flyers yet.</div>
        )}
        {Object.entries(sections).map(([key, label]) => {
          const list = flyers.filter((f) => f.section === key);
          if (!list.length) return null;
          return (
            <div key={key} data-testid={`admin-flyers-section-${key}`}>
              <div className="text-[11px] tracking-[0.22em] uppercase text-bdu-gold mb-3">{label} · {list.length}</div>
              <div className="space-y-2">
                {list.map((f, i) => (
                  <FlyerRow key={f.id} f={f} first={i === 0} last={i === list.length - 1} shareTargets={shareTargets}
                            onChange={load} onEdit={() => setEditing(f)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <EditModal flyer={editing} sections={sections} shareTargets={shareTargets}
                   onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
